import { Suspense } from 'react';

import { type SourcingHiringGoal } from 'api-clients/monolith';

import { useOpeningAnalytics } from '@/contexts/opening-context';
import { cn } from '@/utils/classnames';

import { AggregateHiresControls } from '../aggregate-hires-controls';
import { HiringGoalDateRange } from './date-range';
import { HiringGoalHires } from './hires';
import { NoActiveHiringGoal } from './no-active';

interface HiringGoalViewProps {
  hiringGoal: SourcingHiringGoal | null;
  hiringGoalEditUrl: string;
  onViewPastGoals: () => void;
  hasMultipleActiveGoals?: boolean;
  /** True while switching back to this tab is still being fetched. */
  isPending?: boolean;
  /** The opening's IANA zone; when set, the panel shows it in a chip below the dates. */
  timeZone?: string;
}

type GoalHiresProps = {
  onViewPastGoals: () => void;
  hasMultipleActiveGoals?: boolean;
};

function GoalHires({
  onViewPastGoals,
  hasMultipleActiveGoals,
}: GoalHiresProps) {
  const { hiringGoal } = useOpeningAnalytics();

  return (
    <HiringGoalHires
      actual={hiringGoal?.hire_stats.actual.total ?? null}
      expected={hiringGoal?.hire_stats.expected ?? null}
      onViewPastGoals={onViewPastGoals}
      hasMultipleActiveGoals={hasMultipleActiveGoals}
    />
  );
}

function Divider() {
  return <div className="bg-fx-neutral-200 w-px self-stretch" aria-hidden />;
}

export function HiringGoalView({
  hiringGoal,
  hiringGoalEditUrl,
  onViewPastGoals,
  hasMultipleActiveGoals,
  isPending,
  timeZone,
}: HiringGoalViewProps) {
  if (!hiringGoal?.external_id) {
    return (
      <NoActiveHiringGoal
        addHiringGoalUrl={hiringGoalEditUrl}
        onViewPastGoals={onViewPastGoals}
      />
    );
  }

  return (
    <div className="flex min-h-[80px] items-start gap-6">
      <HiringGoalDateRange
        startDate={hiringGoal.start_date}
        endDate={hiringGoal.hire_by}
        daysElapsed={hiringGoal.days_elapsed}
        addEndDateUrl={hiringGoalEditUrl}
        timeZone={timeZone}
        endAt={hiringGoal.end_at}
      />

      <Divider />

      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col gap-1 transition-opacity',
          isPending && 'opacity-30',
        )}
      >
        <Suspense fallback={<HiringGoalHires.Skeleton />}>
          <GoalHires
            onViewPastGoals={onViewPastGoals}
            hasMultipleActiveGoals={hasMultipleActiveGoals}
          />
        </Suspense>
        <AggregateHiresControls />
      </div>
    </div>
  );
}
